import classNames from 'classnames';
import { sortBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RowComponentProps } from 'react-window';
import { useInfiniteLoader } from 'react-window-infinite-loader';
import { useDimensions } from '../hooks/useDimensions';
import {
  VIRTUALIZED_LOADER_THRESHOLD,
  VIRTUALIZED_ROW_HEIGHT,
} from '../Utils/virtualizationConstants';
import './VirtualizedTable.css';
import {
  VirtualizedTableCellProps,
  VirtualizedTableColumnFit,
  VirtualizedTableKey,
  VirtualizedTableProps,
  VirtualizedTableSelectionType,
  VirtualizedTableSort,
  VirtualizedTableSortDirection,
} from './VirtualizedTable.interface';
import { VirtualizedTableHeader } from './VirtualizedTableHeader';
import { VirtualizedTableLoadingSkeleton } from './VirtualizedTableLoadingSkeleton';
import { VirtualizedTableRow } from './VirtualizedTableRow';
import { VirtualTable } from './VirtualTable';

export interface Viewport {
  width: number | undefined;
  height: number | undefined;
}

/**
 * The VirtualizedTable component is a table that uses virtualization to render only the visible rows and columns.
 * This improves performance for large datasets.
 *
 * @param {VirtualizedTableProps} props - The properties of the component.
 * @returns {JSX.Element} - The rendered component.
 */
export const VirtualizedTable: React.FC<VirtualizedTableProps> = ({
  actions,
  className,
  columnFit = VirtualizedTableColumnFit.Scroll,
  columns,
  defaultSelectedRows = [],
  onBeforeRowEdited,
  onMultiSelect,
  onRowEdited,
  onRowSelectionChange,
  rows,
  selected,
  selectionType = VirtualizedTableSelectionType.None,
  maxSimultaneouslyRows,
  onSortChange,
  rowsTotalCount,
  onLoadMoreItems,
  defaultSort,
  onRowEditCancel,
}) => {
  const isControlled = selected !== undefined;

  const hadKeyboardEvent = useRef<boolean>(false);
  // The available height is measured explicitly instead of relying on the
  // self-sizing of react-window: a consumer whose ancestors have no definite
  // height (flexbox `min-height: auto`) would otherwise make the list grow to
  // the height of all rows, disabling virtualization.
  const wrapperReference = useRef<HTMLDivElement>(null);
  const [wrapperDimensions] = useDimensions<HTMLDivElement>(wrapperReference);

  const [internalSelectedKeys, setInternalSelectedKeys] =
    useState<VirtualizedTableKey[]>(defaultSelectedRows);
  const [sort, setSort] = useState<VirtualizedTableSort | null>(defaultSort ?? null);
  const [focusedRowKey, setFocusedRowKey] = useState<VirtualizedTableKey | null>(null);
  // Key of the row that is currently edited inline.
  // Null if no row is being edited.
  const [rowEditKey, setRowEditKey] = useState<VirtualizedTableKey | null>(null);
  const [viewport, setViewport] = useState<Viewport>({
    width: undefined,
    height: undefined,
  });

  const selectedRowKeys = isControlled ? selected : internalSelectedKeys;

  const setSelectedRowKeys = (newSelected: VirtualizedTableKey[]) => {
    if (!isControlled) {
      setInternalSelectedKeys(newSelected);
    }

    if (onRowSelectionChange) {
      const selectedRows = sortedRows.filter((row) => newSelected.includes(row.key));

      onRowSelectionChange(selectedRows);
    }
  };

  const sortedRows = useMemo(() => {
    if (sort === null || onSortChange) {
      return rows;
    }

    const sortedAsc = sortBy(rows, (row) => row.cells[sort.column].value);

    return sort.direction === VirtualizedTableSortDirection.Asc ? sortedAsc : sortedAsc.reverse();
  }, [onSortChange, rows, sort]);

  const handleColumnHeaderClick = (colIndex: number) => {
    if (columns[colIndex].sorting === false) {
      return;
    }

    const newSortDirection: VirtualizedTableSort = {
      direction:
        sort?.direction === VirtualizedTableSortDirection.Asc
          ? VirtualizedTableSortDirection.Desc
          : VirtualizedTableSortDirection.Asc,
      column: colIndex,
    };

    // Cancel editing if a row is being edited.
    setRowEditKey(null);
    setSort(newSortDirection);

    if (onSortChange) {
      onSortChange(newSortDirection);
    }
  };

  const hasRowIcons = useMemo(() => rows.some((row) => row.icon !== undefined), [rows]);

  const hasEditableColumns = useMemo(() => columns.some((col) => col.editable), [columns]);

  const handleRowClick = (rowKey: VirtualizedTableKey) => {
    if (hasEditableColumns) {
      setRowEditKey(rowKey);
    }
    if (onBeforeRowEdited) {
      onBeforeRowEdited(rowKey);
    }
  };

  const calulateViewport = (width: number, height: number) => {
    if (viewport.width === undefined || viewport.height === undefined) {
      if (width === viewport.width && height === viewport.height) {
        return;
      }
      setViewport({ width, height });
    } else {
      const newWidth = Math.min(viewport.width, width);
      const newHeight = Math.min(viewport.height, height);

      if (newWidth === viewport.width && newHeight === viewport.height) {
        return;
      }

      setViewport({
        width: Math.min(viewport.width, width),
        height: Math.min(viewport.height, height),
      });
    }
  };

  const handleRowEditCancel = (rowKey: VirtualizedTableKey) => {
    setRowEditKey(null);
    if (onRowEditCancel) {
      onRowEditCancel(rowKey);
    }
  };

  const handleRowEditSave = (
    rowKey: VirtualizedTableKey,
    editedCells: VirtualizedTableCellProps[],
  ) => {
    if (onRowEdited) {
      const result = onRowEdited(rowKey, editedCells);
      if (result === false) {
        // If the row edit was not successful, we do not change the edit key.
        return;
      }
    }

    setRowEditKey(null);
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Ignore keypresses if the user is holding down a modifier key.
      if (!event.altKey && !event.ctrlKey && !event.metaKey) {
        hadKeyboardEvent.current = true;
      }
    };

    function onPointerDown() {
      hadKeyboardEvent.current = false;
    }

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointerDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown, true);
    };
  }, []);

  const Row = ({ index: rowIndex, style }: RowComponentProps) => {
    return (
      <>
        {sortedRows[rowIndex] && (
          <VirtualizedTableRow
            rowIndex={rowIndex}
            style={style}
            hasRowIcons={hasRowIcons}
            selectionType={selectionType}
            row={sortedRows[rowIndex]}
            columns={columns}
            actions={actions}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            editMode={rowEditKey === sortedRows[rowIndex].key}
            focused={hadKeyboardEvent.current && focusedRowKey === sortedRows[rowIndex].key}
            onDoubleClick={
              sortedRows[rowIndex].isEditable !== false && hasEditableColumns
                ? () => handleRowClick(rows[rowIndex].key)
                : undefined
            }
            onFocus={(rowKey) => {
              hadKeyboardEvent.current && setFocusedRowKey(rowKey);
            }}
            className={classNames({
              editmode: rowIndex === rowEditKey,
              readonly: rowIndex !== rowEditKey,
              isEditable: sortedRows[rowIndex].isEditable !== false && hasEditableColumns,
            })}
            viewport={viewport}
            onEditCancelButtonClick={handleRowEditCancel}
            onEditSaveButtonClick={handleRowEditSave}
            onMultiSelectClick={(cellIndex: number) => {
              if (!onMultiSelect) {
                return;
              }

              onMultiSelect(rows[rowIndex].key, cellIndex);
            }}
            columnFit={columnFit}
          />
        )}
        {!sortedRows[rowIndex] && (
          <VirtualizedTableLoadingSkeleton
            style={style}
            rowIndex={rowIndex}
            selectionType={selectionType}
            columns={columns}
            columnFit={columnFit}
            hasRowIcons={hasRowIcons}
            actions={actions}
          />
        )}
      </>
    );
  };

  const Header = (
    <VirtualizedTableHeader
      columns={columns}
      actions={actions}
      sort={sort}
      hasRowIcons={hasRowIcons}
      selectionType={selectionType}
      onColumnHeaderClick={handleColumnHeaderClick}
      columnFit={columnFit}
    />
  );

  const maxHeight = useMemo(() => {
    // + 1 for the header row, which is one row tall.
    return maxSimultaneouslyRows
      ? (Math.min(maxSimultaneouslyRows, sortedRows.length) + 1) * VIRTUALIZED_ROW_HEIGHT
      : undefined;
  }, [maxSimultaneouslyRows, sortedRows.length]);

  // Identity changes whenever the loaded rows change, which resets the
  // InfiniteLoader's internal "already requested" cache. This replaces the
  // `resetloadMoreItemsCache` call of the v1 component API.
  const handleIsRowLoaded = useCallback(
    (index: number): boolean => !!sortedRows[index],
    [sortedRows],
  );

  const handleLoadMoreRows = useCallback(
    async (startIndex: number, stopIndex: number): Promise<void> => {
      if (onLoadMoreItems) {
        onLoadMoreItems(startIndex, stopIndex);
      }
    },
    [onLoadMoreItems],
  );

  const onRowsRendered = useInfiniteLoader({
    isRowLoaded: handleIsRowLoaded,
    loadMoreRows: handleLoadMoreRows,
    rowCount: rowsTotalCount ?? sortedRows.length,
    threshold: VIRTUALIZED_LOADER_THRESHOLD,
  });

  return (
    <div
      ref={wrapperReference}
      style={{
        // The list is positioned absolutely so that its own height never feeds
        // back into the measured height of this wrapper. Without that
        // decoupling a consumer whose ancestors have no definite height turns
        // measure → resize → measure into an endless ResizeObserver loop.
        position: 'relative',
        height: '100%',
        width: '100%',
        minHeight: maxHeight ? maxHeight + 'px' : undefined,
        overflow: 'hidden',
      }}
      aria-colcount={columns.length}
      aria-rowcount={rowsTotalCount ?? rows.length}
      role="table"
    >
      {wrapperDimensions !== null && (
        <VirtualTable
          rowCount={rowsTotalCount ?? (sortedRows.length > 0 ? sortedRows.length : 5)}
          header={Header}
          row={Row}
          rowHeight={VIRTUALIZED_ROW_HEIGHT}
          className={classNames('virtualized-table-container', className)}
          columnFit={columnFit}
          onSizeChanged={calulateViewport}
          onRowsRendered={onRowsRendered}
          // `position` has to be set here rather than in the stylesheet:
          // react-window applies `position: relative` as an inline style, so only
          // another inline style can win. Absolute keeps the list's height out of
          // the wrapper's content height — otherwise measuring the wrapper and
          // sizing the list from it feed each other in an endless loop.
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: wrapperDimensions.height,
            width: wrapperDimensions.width,
          }}
        />
      )}
    </div>
  );
};
